import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { areasOfExpertise } from "config";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { usePatchCvMutation } from "../../../lib/cvsSlice";

const LEVELS = ["Novice", "Beginner", "Intermediate", "Advanced", "Expert"];

const Expertise = ({ expertise, cvId }) => {
  const [skills, setSkills] = useState(Array.isArray(expertise) ? expertise : []);
  const [searchValue, setSearchValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const isDirty = useRef(false);

  const [patchCv] = usePatchCvMutation();
  // Separate instance so its pending state tracks only the add-skill save.
  const [addSkill, { isLoading: isAddingSkill }] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  // Guard against the 1-second poll in CvDetails resetting local state
  // while the user is interacting with the skill list.
  useEffect(() => {
    if (isDirty.current) return;
    setSkills(Array.isArray(expertise) ? expertise : []);
  }, [expertise, cvId]);

  const addedSkillNames = skills.flatMap((s) => s.expertise);

  const handleAddSkill = async (option) => {
    if (!option) return;
    isDirty.current = true;
    const skillName = typeof option === "string" ? option.trim() : option.value;
    if (!skillName) return;

    if (addedSkillNames.includes(skillName)) {
      toast.info("Skill already added");
      setSearchValue(null);
      setInputValue("");
      return;
    }

    try {
      const result = await addSkill({
        cvId,
        expertise: [{ expertise: [skillName] }],
      }).unwrap();

      // Find the newly added subdoc (last one matching skillName without _id)
      const newSkill = result?.expertise?.find(
        (e) => e.expertise.includes(skillName) && !skills.find((s) => s._id === e._id)
      );
      if (newSkill) {
        setSkills((prev) => [...prev, newSkill]);
      } else {
        setSkills(result?.expertise ?? skills);
      }
    } catch (err) {
      toast.error(`Failed to add skill: ${err?.data?.message ?? err.message}`);
    }

    setSearchValue(null);
    setInputValue("");
  };

  const handleRemove = async (skillId) => {
    isDirty.current = true;
    try {
      await patchCv({ cvId, expertise: [{ id: skillId, delete: true }] }).unwrap();
      setSkills((prev) => prev.filter((s) => s._id !== skillId));
    } catch (err) {
      toast.error(`Failed to remove: ${err?.data?.message ?? err.message}`);
    }
  };

  const handleLevelChange = async (skillId, newLevel) => {
    isDirty.current = true;
    const skill = skills.find((s) => s._id === skillId);
    if (!skill) return;

    setSkills((prev) =>
      prev.map((s) => (s._id === skillId ? { ...s, level: newLevel } : s))
    );

    try {
      await patchCv({
        cvId,
        expertise: [{ id: skillId, level: newLevel }],
      }).unwrap();
    } catch (err) {
      // Revert on failure
      setSkills((prev) =>
        prev.map((s) => (s._id === skillId ? skill : s))
      );
      toast.error(`Failed to update level: ${err?.data?.message ?? err.message}`);
    }
  };

  // NEXT stays disabled while any added skill has no level chosen, and
  // while an add is in flight (the new row isn't in the list yet).
  const hasUnsetLevel = skills.some((s) => !s.level);
  const nextDisabled = hasUnsetLevel || isAddingSkill;

  const handleNext = () => {
    goToNextStep();
  };

  const filteredOptions = areasOfExpertise.filter(
    (opt) => !addedSkillNames.includes(opt.value)
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

      {/* Search */}
      <Autocomplete
        options={filteredOptions}
        getOptionLabel={(opt) => typeof opt === "string" ? opt : opt.label}
        value={searchValue}
        inputValue={inputValue}
        open={autocompleteOpen}
        onOpen={() => { if (inputValue.length >= 2) setAutocompleteOpen(true); }}
        onClose={() => setAutocompleteOpen(false)}
        onInputChange={(_, val) => {
          setInputValue(val);
          setAutocompleteOpen(val.length >= 2);
        }}
        freeSolo
        onChange={(_, opt) => handleAddSkill(opt)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for Skill"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E" }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        noOptionsText="No matching skills found"
      />

      {/* Skills list */}
      {skills.length > 0 && (
        <Box
          sx={{
            border: "1px solid #E0DEDD",
            borderRadius: "8px",
            backgroundColor: "#F4F2F1",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E0DEDD" }}>
            <Typography
              sx={{ fontSize: "13px", color: "#7A706F", fontFamily: "Inter, sans-serif" }}
            >
              {skills.length} Skill{skills.length !== 1 ? "s" : ""} added
            </Typography>
          </Box>

          {/* Rows */}
          {skills.map((skill) => (
            <Box
              key={skill._id}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #E0DEDD",
                backgroundColor: "#fff",
                "&:last-child": { borderBottom: "none" },
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  color: "#1A1A1A",
                }}
              >
                {skill.expertise.join(", ")}
              </Typography>

              <Select
                value={skill.level ?? ""}
                displayEmpty
                onChange={(e) => handleLevelChange(skill._id, e.target.value)}
                size="small"
                sx={{
                  minWidth: 130,
                  fontSize: "14px",
                  borderRadius: "6px",
                  backgroundColor: "#fff",
                }}
              >
                <MenuItem value="" disabled sx={{ fontSize: "14px" }}>
                  Select level
                </MenuItem>
                {LEVELS.map((lvl) => (
                  <MenuItem key={lvl} value={lvl} sx={{ fontSize: "14px" }}>
                    {lvl}
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size="small"
                onClick={() => handleRemove(skill._id)}
                sx={{ color: "#9E9E9E", "&:hover": { color: "#E84610" } }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={nextDisabled}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            "&:disabled": { backgroundColor: "#E8A090" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          NEXT
        </Button>
      </Box>
    </Box>
  );
};

export default Expertise;
